import { showToast } from "@/services/toast";

type BillingErrorDetail = string | { code?: string; message?: string };

function normalizeBillingError(detail: BillingErrorDetail) {
  const code = typeof detail === "string" ? "" : String(detail?.code ?? "");
  const message = typeof detail === "string" ? detail : String(detail?.message ?? code);
  return { code, message, normalized: `${code} ${message}`.toLowerCase() };
}

export function isCreditErrorDetail(errorDetail: BillingErrorDetail) {
  const { normalized } = normalizeBillingError(errorDetail);
  return (
    normalized.includes("credit check failed") ||
    normalized.includes("trial expired") ||
    normalized.includes("insufficient credits") ||
    normalized.includes("payment required") ||
    normalized.includes("organisation credits exhausted") ||
    normalized.includes("organization credits exhausted") ||
    normalized.includes("balance exhausted") ||
    normalized.includes("gcp_marketplace_")
  );
}

function billingProfileHref(): string {
  if (typeof window === "undefined") return "/profile?tab=billing";
  const liveDemo = window.location.pathname.startsWith("/live-demo");
  return liveDemo ? "/live-demo/profile?tab=billing" : "/profile?tab=billing";
}

function showCreditToast(
  message: string,
  toastId: string,
  opts?: { withUpgrade?: boolean },
) {
  // Always call showToast with a stable toastId — react-toastify prevents
  // stacking duplicates. Avoid toast.isActive() early-returns: during the
  // close animation isActive can stay true and swallow the next show.
  showToast(message, "error", {
    toastId,
    icon: false,
    autoClose: 12000,
    ...(opts?.withUpgrade
      ? {
          actionLabel: "Upgrade",
          actionHref: billingProfileHref(),
        }
      : {}),
  });
}

export function handleCreditError(errorDetail: BillingErrorDetail) {
  const { code, message, normalized } = normalizeBillingError(errorDetail);
  if (code.startsWith("gcp_marketplace_")) {
    showCreditToast(message || "Google Cloud Marketplace access is unavailable.", code);
    return;
  }

  // Trial expiration - specific message and action
  if (normalized.includes("trial expired")) {
    showCreditToast(
      "Your free trial has ended. Upgrade to continue using NomadicAI.",
      "credit-error-trial-expired",
      { withUpgrade: true },
    );
    return;
  }

  // Organization credits exhausted
  if (
    normalized.includes("organisation credits exhausted") ||
    normalized.includes("organization credits exhausted")
  ) {
    showCreditToast(
      "Your organization has run out of credits. Please contact your admin.",
      "credit-error-org-exhausted",
    );
    return;
  }

  // Personal credits exhausted (paid user)
  if (normalized.includes("balance exhausted")) {
    showCreditToast(
      "You're out of credits. Add more to continue analyzing videos.",
      "credit-error-balance-exhausted",
      { withUpgrade: true },
    );
    return;
  }

  // Insufficient credits (shows exact numbers)
  if (normalized.includes("insufficient credits")) {
    // Backend sends: "Insufficient credits. You have X credits, but this analysis requires Y credits."
    showCreditToast(message, "credit-error-insufficient", {
      withUpgrade: true,
    });
    return;
  }

  // Generic fallback for any other 402 messages
  showCreditToast(
    "Credits required. Please check your billing settings.",
    "credit-error-generic",
    { withUpgrade: true },
  );
}
